"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { DraggableCardBody, DraggableCardContainer } from "@/components/ui/draggable-card";
import { ArrowRight, Camera, Users, Clock, Sparkles, Repeat } from "lucide-react";
import { motion, useMotionValue, useAnimationControls } from "motion/react";
import { useConvex } from "convex/react";
import { api } from "socialloop-convex/api";
// Auto-reset draggable card component
const AutoResetDraggableCard = ({
  children,
  className,
  initialRotation = 'rotate(0deg)',
  resetDelay = 3000
}: {
  children: React.ReactNode;
  className?: string;
  initialRotation?: string;
  resetDelay?: number;
}) => {
  const resetTimer = useRef<NodeJS.Timeout | null>(null);
  const cardRef = useRef<any>(null);

  // Custom DraggableCardBody with reset functionality
  const CustomDraggableCardBody = ({ className, children }: { className?: string; children?: React.ReactNode }) => {
    const mouseX = useMotionValue(0);
    const mouseY = useMotionValue(0);
    const dragRef = useRef<HTMLDivElement>(null);
    const controls = useAnimationControls();
    const [constraints, setConstraints] = useState({
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    });

    // Extract rotation value from initialRotation string
    const rotationValue = parseFloat(initialRotation.match(/-?\d+/)?.[0] || '0');

    // Store reference for reset functionality
    useEffect(() => {
      cardRef.current = { mouseX, mouseY, controls, rotationValue };
    }, [mouseX, mouseY, controls, rotationValue]);

    const updateConstraints = useCallback(() => {
      if (typeof window !== "undefined" && dragRef.current) {
        const cardRect = dragRef.current.getBoundingClientRect();
        const cardWidth = cardRect.width;
        const cardHeight = cardRect.height;

        const padding = 50;
        setConstraints({
          top: padding - cardRect.top,
          left: padding - cardRect.left,
          right: window.innerWidth - cardWidth - padding - cardRect.left,
          bottom: window.innerHeight - cardHeight - padding - cardRect.top,
        });
      }
    }, []);

    useEffect(() => {
      const timeoutId = setTimeout(updateConstraints, 100);
      const handleResize = () => requestAnimationFrame(updateConstraints);
      window.addEventListener("resize", handleResize, { passive: true });

      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener("resize", handleResize);
      };
    }, [updateConstraints]);

    const handleDragEnd = () => {
      // Clear existing timer
      if (resetTimer.current) {
        clearTimeout(resetTimer.current);
      }

      // Set new timer to reset position after specified delay
      resetTimer.current = setTimeout(() => {
        if (cardRef.current) {
          // Animate back to original position using Framer Motion, preserving rotation
          cardRef.current.controls.start({
            x: 0,
            y: 0,
            rotate: cardRef.current.rotationValue,
            transition: {
              type: "spring",
              stiffness: 100,
              damping: 20,
              mass: 0.5,
            },
          });
        }
      }, resetDelay);
    };

    const handleDragStart = () => {
      // Clear reset timer when dragging starts
      if (resetTimer.current) {
        clearTimeout(resetTimer.current);
      }
    };

    return (
      <motion.div
        ref={dragRef}
        drag
        dragConstraints={constraints}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        animate={controls}
        whileHover={{ scale: 1.02 }}
        initial={{ rotate: rotationValue }}
        style={{
          willChange: "transform",
          opacity: 1, // Full opacity
          rotate: rotationValue, // Set base rotation
        }}
        className={`relative min-h-96 w-80 overflow-hidden rounded-md bg-neutral-100 p-6 shadow-2xl transform-3d dark:bg-neutral-900 ${className}`}
      >
        {children}
      </motion.div>
    );
  };

  useEffect(() => {
    return () => {
      if (resetTimer.current) {
        clearTimeout(resetTimer.current);
      }
    };
  }, []);

  return <CustomDraggableCardBody className={className}>{children}</CustomDraggableCardBody>;
};

// Mock event photos that convey the reloop message
const reloopMockPhotos = [
  {
    image: "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=800&h=600&fit=crop",
    title: "Epic Moments",
    subtitle: "Relive the Fun"
  },
  {
    image: "https://images.unsplash.com/photo-1515187029135-18ee286d815b?w=800&h=600&fit=crop",
    title: "Team Vibes",
    subtitle: "Loop the Energy"
  },
  {
    image: "https://images.unsplash.com/photo-1559223607-b4d0555ae227?w=800&h=600&fit=crop",
    title: "Creative Flow",
    subtitle: "Rewind & Share"
  },
  {
    image: "https://images.unsplash.com/photo-1517048676732-d0b86b9b2681?w=800&h=600&fit=crop",
    title: "Success Stories",
    subtitle: "Loop the Win"
  },
  {
    image: "https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?w=800&h=600&fit=crop",
    title: "Learning Together",
    subtitle: "Replay Insights"
  },
  {
    image: "https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=800&h=600&fit=crop",
    title: "New Connections",
    subtitle: "Reloop Bonds"
  }
];

export default function ReloopPage() {
  const [mounted, setMounted] = useState(false);
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const router = useRouter();
  const convex = useConvex();
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);



  useEffect(() => {
    setMounted(true);
  }, []);

  const handleCodeChange = (index: number, value: string) => {
    if (value.length > 1) return; // Only allow single digit

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async () => {
    const fullCode = code.join('');
    if (fullCode.length !== 6) {
      setError('Please enter a complete 6-digit code');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Validate event code via Convex
      const event = await convex.query(api.events.getByCode, { code: fullCode });

      if (event) {
        // Navigate to the actual event gallery
        router.push(`/reloop/${event._id}`);
      } else {
        setError('Invalid code. Please check with event organizer.');
        setCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (error) {
      console.error('Code validation error:', error);
      // Fallback: if code is demo code, go to demo
      if (fullCode === '123456') {
        router.push('/reloop/demo');
      } else {
        setError('Invalid code. Please check with event organizer.');
        setCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!mounted) {
    return null;
  }

  return (
    <div className="min-h-screen bg-white overflow-hidden relative">
      {/* Hero Content */}
      <div className="absolute inset-0 flex items-center justify-center z-50 px-4 sm:px-6 lg:px-8 pointer-events-none">
        <div className="text-center max-w-2xl mx-auto w-full pointer-events-auto">
          {/* Main Title */}
          <div className="mb-6 sm:mb-8">
            <div className="bg-yellow-400 border-4 sm:border-8 border-black p-4 sm:p-6 lg:p-8 transform -rotate-2 mb-4 sm:mb-6 inline-block">
              <h1 className="text-3xl sm:text-5xl lg:text-7xl font-black text-black tracking-tight uppercase">
                RELOOP
              </h1>
            </div>
            <div className="bg-blue-400 border-2 sm:border-4 border-black p-3 sm:p-4 transform rotate-1 inline-block">
              <p className="text-sm sm:text-xl lg:text-2xl text-black font-bold uppercase">
                Relive Event Moments
              </p>
            </div>
          </div>

          {/* Reloop Message */}
          <div className="bg-green-400 border-2 sm:border-4 border-black p-4 sm:p-6 transform -rotate-1 mb-4 sm:mb-6">
            <div className="flex items-center justify-center gap-2 sm:gap-3 mb-2">
              <Repeat className="w-4 h-4 sm:w-6 sm:h-6 text-black" />
              <p className="text-sm sm:text-lg font-black text-black uppercase">Loop the Fun</p>
              <Sparkles className="w-4 h-4 sm:w-6 sm:h-6 text-black" />
            </div>
            <p className="text-xs sm:text-sm font-bold text-black">
              Rewind, replay, and share your favorite event memories
            </p>
          </div>

          {/* Description */}
          <div className="bg-pink-400 border-2 sm:border-4 border-black p-4 sm:p-6 transform rotate-1 mb-6 sm:mb-8">
            <p className="text-sm sm:text-lg font-bold text-black">
              Enter your 6-digit event code to start relooping
            </p>
          </div>

          {/* Animated Arrows pointing to code input */}
          <div className="flex justify-center gap-8 mb-4">
            <motion.div
              animate={{
                y: [0, -10, 0],
                rotate: [0, 5, 0]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="bg-green-400 border-4 border-black p-2 transform rotate-12"
            >
              <ArrowRight className="w-6 h-6 text-black transform rotate-90" />
            </motion.div>
            <motion.div
              animate={{
                y: [0, -10, 0],
                rotate: [0, -5, 0]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 0.5
              }}
              className="bg-purple-400 border-4 border-black p-2 transform -rotate-12"
            >
              <ArrowRight className="w-6 h-6 text-black transform rotate-90" />
            </motion.div>
          </div>

          {/* Code Input */}
          <div className="bg-white border-4 sm:border-8 border-black p-4 sm:p-6 lg:p-8 transform -rotate-1 shadow-[8px_8px_0px_0px_#000000] sm:shadow-[16px_16px_0px_0px_#000000] mb-4 sm:mb-6 relative">
            <div className="flex justify-center gap-2 sm:gap-3 mb-4 sm:mb-6">
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => {
                    inputRefs.current[index] = el;
                  }}
                  type="text"
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 text-center text-lg sm:text-2xl lg:text-3xl font-black border-2 sm:border-4 border-black bg-gray-100 focus:bg-yellow-400 focus:outline-none transition-colors duration-200 hover:scale-105 focus:scale-110 focus:rotate-1"
                  maxLength={1}
                />
              ))}
            </div>

            {error && (
              <div className="bg-red-400 border-2 sm:border-4 border-black p-2 sm:p-3 mb-3 sm:mb-4 transform rotate-1">
                <p className="font-bold text-black text-center text-sm sm:text-base">{error}</p>
              </div>
            )}

            <motion.button
              onClick={handleSubmit}
              disabled={isLoading || code.join('').length !== 6}
              className="w-full sm:w-auto bg-green-400 hover:bg-black hover:text-white border-2 sm:border-4 border-black p-3 sm:p-4 font-black text-black uppercase transform hover:rotate-0 transition-all duration-200 shadow-[4px_4px_0px_0px_#000000] sm:shadow-[8px_8px_0px_0px_#000000] hover:shadow-[2px_2px_0px_0px_#000000] sm:hover:shadow-[4px_4px_0px_0px_#000000] active:shadow-[1px_1px_0px_0px_#000000] sm:active:shadow-[2px_2px_0px_0px_#000000] disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
              whileHover={{ scale: 1.05, rotate: 1 }}
              whileTap={{ scale: 0.95 }}
              animate={isLoading ? { rotate: [0, 5, -5, 0] } : {}}
              transition={{ duration: 0.5, repeat: isLoading ? Infinity : 0 }}
            >
              {isLoading ? (
                <motion.span
                  animate={{ opacity: [1, 0.5, 1] }}
                  transition={{ duration: 1, repeat: Infinity }}
                >
                  VALIDATING...
                </motion.span>
              ) : (
                <>
                  <ArrowRight className="w-4 h-4 sm:w-6 sm:h-6 mr-2 inline" />
                  ACCESS EVENT
                </>
              )}
            </motion.button>
          </div>

          {/* Demo Code Hint */}
          <div className="bg-purple-400 border-2 sm:border-4 border-black p-3 sm:p-4 transform rotate-1">
            <p className="text-xs sm:text-sm font-bold text-black">
              Demo Code: <span className="font-black">123456</span> | Your Event: <span className="font-black">654321</span>
            </p>
          </div>
        </div>
      </div>

      {/* Background Decorative Cards - Interactive */}
      <DraggableCardContainer className="absolute inset-0 z-40 hidden sm:block">
        {/* Left Side Decorative Cards */}
        <div className="absolute top-16 sm:top-32 left-2 sm:left-8">
          <AutoResetDraggableCard
            className="relative w-48 h-56 sm:w-72 sm:h-80 bg-white border-4 sm:border-8 border-black shadow-[6px_6px_0px_0px_#000000] sm:shadow-[12px_12px_0px_0px_#000000]"
            initialRotation="rotate(-8deg)"
            resetDelay={3000}
          >
            <div className="relative h-full overflow-hidden pointer-events-none">
              <div className="relative h-4/5 w-full">
                <Image
                  src={reloopMockPhotos[0].image}
                  alt={reloopMockPhotos[0].title}
                  fill
                  className="object-cover pointer-events-none"
                />
              </div>
              <div className="h-1/5 bg-yellow-400 border-t-2 sm:border-t-4 border-black p-1 sm:p-2 flex flex-col justify-center pointer-events-none">
                <h3 className="font-black text-black text-xs sm:text-sm uppercase truncate">
                  {reloopMockPhotos[0].title}
                </h3>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs font-bold text-black truncate">{reloopMockPhotos[0].subtitle}</span>
                  <div className="flex items-center gap-1">
                    <Repeat className="w-2 h-2 sm:w-3 sm:h-3 text-black" />
                  </div>
                </div>
              </div>
            </div>
          </AutoResetDraggableCard>
        </div>

        <div className="absolute top-64 sm:top-80 left-16 sm:left-32">
          <AutoResetDraggableCard
            className="relative w-48 h-56 sm:w-72 sm:h-80 bg-white border-4 sm:border-8 border-black shadow-[6px_6px_0px_0px_#000000] sm:shadow-[12px_12px_0px_0px_#000000]"
            initialRotation="rotate(5deg)"
            resetDelay={3000}
          >
            <div className="relative h-full overflow-hidden pointer-events-none">
              <div className="relative h-4/5 w-full">
                <Image
                  src={reloopMockPhotos[1].image}
                  alt={reloopMockPhotos[1].title}
                  fill
                  className="object-cover pointer-events-none"
                />
              </div>
              <div className="h-1/5 bg-pink-400 border-t-2 sm:border-t-4 border-black p-1 sm:p-2 flex flex-col justify-center pointer-events-none">
                <h3 className="font-black text-black text-xs sm:text-sm uppercase truncate">
                  {reloopMockPhotos[1].title}
                </h3>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs font-bold text-black truncate">{reloopMockPhotos[1].subtitle}</span>
                  <div className="flex items-center gap-1">
                    <Camera className="w-2 h-2 sm:w-3 sm:h-3 text-black" />
                  </div>
                </div>
              </div>
            </div>
          </AutoResetDraggableCard>
        </div>

        <div className="absolute bottom-16 sm:bottom-32 left-2 sm:left-12">
          <AutoResetDraggableCard
            className="relative w-48 h-56 sm:w-72 sm:h-80 bg-white border-4 sm:border-8 border-black shadow-[6px_6px_0px_0px_#000000] sm:shadow-[12px_12px_0px_0px_#000000]"
            initialRotation="rotate(-12deg)"
            resetDelay={3000}
          >
            <div className="relative h-full overflow-hidden pointer-events-none">
              <div className="relative h-4/5 w-full">
                <Image
                  src={reloopMockPhotos[2].image}
                  alt={reloopMockPhotos[2].title}
                  fill
                  className="object-cover pointer-events-none"
                />
              </div>
              <div className="h-1/5 bg-green-400 border-t-2 sm:border-t-4 border-black p-1 sm:p-2 flex flex-col justify-center pointer-events-none">
                <h3 className="font-black text-black text-xs sm:text-sm uppercase truncate">
                  {reloopMockPhotos[2].title}
                </h3>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs font-bold text-black truncate">{reloopMockPhotos[2].subtitle}</span>
                  <div className="flex items-center gap-1">
                    <Sparkles className="w-2 h-2 sm:w-3 sm:h-3 text-black" />
                  </div>
                </div>
              </div>
            </div>
          </AutoResetDraggableCard>
        </div>

        {/* Right Side Decorative Cards */}
        <div className="absolute top-16 sm:top-32 right-2 sm:right-8">
          <AutoResetDraggableCard
            className="relative w-48 h-56 sm:w-72 sm:h-80 bg-white border-4 sm:border-8 border-black shadow-[6px_6px_0px_0px_#000000] sm:shadow-[12px_12px_0px_0px_#000000]"
            initialRotation="rotate(10deg)"
            resetDelay={3000}
          >
            <div className="relative h-full overflow-hidden pointer-events-none">
              <div className="relative h-4/5 w-full">
                <Image
                  src={reloopMockPhotos[3].image}
                  alt={reloopMockPhotos[3].title}
                  fill
                  className="object-cover pointer-events-none"
                />
              </div>
              <div className="h-1/5 bg-blue-400 border-t-2 sm:border-t-4 border-black p-1 sm:p-2 flex flex-col justify-center pointer-events-none">
                <h3 className="font-black text-black text-xs sm:text-sm uppercase truncate">
                  {reloopMockPhotos[3].title}
                </h3>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs font-bold text-black truncate">{reloopMockPhotos[3].subtitle}</span>
                  <div className="flex items-center gap-1">
                    <Users className="w-2 h-2 sm:w-3 sm:h-3 text-black" />
                  </div>
                </div>
              </div>
            </div>
          </AutoResetDraggableCard>
        </div>

        <div className="absolute top-64 sm:top-80 right-16 sm:right-32">
          <AutoResetDraggableCard
            className="relative w-48 h-56 sm:w-72 sm:h-80 bg-white border-4 sm:border-8 border-black shadow-[6px_6px_0px_0px_#000000] sm:shadow-[12px_12px_0px_0px_#000000]"
            initialRotation="rotate(-6deg)"
            resetDelay={3000}
          >
            <div className="relative h-full overflow-hidden pointer-events-none">
              <div className="relative h-4/5 w-full">
                <Image
                  src={reloopMockPhotos[4].image}
                  alt={reloopMockPhotos[4].title}
                  fill
                  className="object-cover pointer-events-none"
                />
              </div>
              <div className="h-1/5 bg-orange-400 border-t-2 sm:border-t-4 border-black p-1 sm:p-2 flex flex-col justify-center pointer-events-none">
                <h3 className="font-black text-black text-xs sm:text-sm uppercase truncate">
                  {reloopMockPhotos[4].title}
                </h3>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs font-bold text-black truncate">{reloopMockPhotos[4].subtitle}</span>
                  <div className="flex items-center gap-1">
                    <Clock className="w-2 h-2 sm:w-3 sm:h-3 text-black" />
                  </div>
                </div>
              </div>
            </div>
          </AutoResetDraggableCard>
        </div>

        <div className="absolute bottom-16 sm:bottom-32 right-2 sm:right-12">
          <AutoResetDraggableCard
            className="relative w-48 h-56 sm:w-72 sm:h-80 bg-white border-4 sm:border-8 border-black shadow-[6px_6px_0px_0px_#000000] sm:shadow-[12px_12px_0px_0px_#000000]"
            initialRotation="rotate(8deg)"
            resetDelay={3000}
          >
            <div className="relative h-full overflow-hidden pointer-events-none">
              <div className="relative h-4/5 w-full">
                <Image
                  src={reloopMockPhotos[5].image}
                  alt={reloopMockPhotos[5].title}
                  fill
                  className="object-cover pointer-events-none"
                />
              </div>
              <div className="h-1/5 bg-red-400 border-t-2 sm:border-t-4 border-black p-1 sm:p-2 flex flex-col justify-center pointer-events-none">
                <h3 className="font-black text-black text-xs sm:text-sm uppercase truncate">
                  {reloopMockPhotos[5].title}
                </h3>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs font-bold text-black truncate">{reloopMockPhotos[5].subtitle}</span>
                  <div className="flex items-center gap-1">
                    <Repeat className="w-2 h-2 sm:w-3 sm:h-3 text-black" />
                  </div>
                </div>
              </div>
            </div>
          </AutoResetDraggableCard>
        </div>
      </DraggableCardContainer>

      {/* Neubrutalist Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        {/* Grid pattern */}
        <div className="absolute inset-0 sm:hidden" style={{
          backgroundImage: `
            linear-gradient(black 1px, transparent 1px),
            linear-gradient(90deg, black 1px, transparent 1px)
          `,
          backgroundSize: '20px 20px'
        }}></div>
        <div className="absolute inset-0 hidden sm:block" style={{
          backgroundImage: `
            linear-gradient(black 2px, transparent 2px),
            linear-gradient(90deg, black 2px, transparent 2px)
          `,
          backgroundSize: '40px 40px'
        }}></div>

        {/* Animated floating particles */}
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className={`hidden sm:block absolute w-3 h-3 border-2 border-black ${
              ['bg-yellow-400', 'bg-pink-400', 'bg-blue-400', 'bg-green-400', 'bg-purple-400', 'bg-red-400', 'bg-orange-400', 'bg-cyan-400'][i]
            }`}
            initial={{
              x: Math.random() * window?.innerWidth || 1200,
              y: Math.random() * window?.innerHeight || 800,
            }}
            animate={{
              x: Math.random() * (window?.innerWidth || 1200),
              y: Math.random() * (window?.innerHeight || 800),
              rotate: [0, 360],
            }}
            transition={{
              duration: 10 + Math.random() * 10,
              repeat: Infinity,
              ease: "linear",
              delay: i * 0.5,
            }}
          />
        ))}

        {/* Scattered geometric shapes - now animated */}
        <motion.div
          className="hidden sm:block absolute top-20 left-1/4 w-16 h-16 bg-yellow-400 border-4 border-black"
          animate={{ rotate: [0, 45, 90, 45, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="hidden sm:block absolute top-40 right-1/3 w-12 h-12 bg-pink-400 border-4 border-black rounded-full"
          animate={{ scale: [1, 1.2, 1], y: [0, -10, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="hidden sm:block absolute bottom-40 left-1/3 w-20 h-8 bg-blue-400 border-4 border-black"
          animate={{ x: [0, 20, 0], rotate: [0, 10, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="hidden sm:block absolute bottom-60 right-1/4 w-14 h-14 bg-green-400 border-4 border-black"
          animate={{ rotate: [12, 45, 12], scale: [1, 0.9, 1] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="hidden sm:block absolute top-1/2 left-1/2 w-10 h-20 bg-purple-400 border-4 border-black"
          animate={{ rotate: [-45, -30, -45], x: [0, 15, 0] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>
    </div>
  );
}